import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { AlertCircle, AlertTriangle, ScanSearch } from 'lucide-react';
import { parseDtcInput } from '../../../lib/dtcDecoder/parser';
import { dtcBytesToCode, dtcCodeToBytes, dtcBitFields, describeDtc, decodeFtb } from '../../../lib/dtcDecoder/dtc';
import { statusVerdict } from '../../../lib/dtcDecoder/statusByte';
import { hex2, bytesToHex } from '../../../lib/dtcDecoder/constants';
import { CopyButton, SectionLabel, VERDICT_TONE } from './shared';

const SCOPE_BADGE = {
  generic: 'text-emerald-700 dark:text-emerald-400 border-emerald-600/40 bg-emerald-600/10',
  manufacturer: 'text-amber-700 dark:text-amber-400 border-amber-600/40 bg-amber-600/10',
  reserved: 'text-muted-foreground border-muted-foreground/30 bg-muted/40',
};
const SCOPE_TEXT = { generic: 'Generic (ISO/SAE)', manufacturer: 'Manufacturer-specific', reserved: 'Reserved' };

// Turns the tab's own input into { code, ftb, status } or { ftbOnly } or { error }.
function interpret(raw) {
  const r = parseDtcInput(raw);
  if (!r.ok) return { error: r.error };
  if (r.kind === 'dtcCode') return { code: r.code, ftb: r.ftb };
  if (r.kind === 'dtcBytes') return { code: dtcBytesToCode(r.dtcBytes), ftb: r.ftb, status: r.status };
  if (r.kind === 'ambiguousByte') return { ftbOnly: r.value };
  if (r.bytes && r.bytes.length >= 2 && r.bytes.length <= 4) {
    const f = parseDtcInput(raw, { force: 'dtc' });
    if (f.ok) return { code: dtcBytesToCode(f.dtcBytes), ftb: f.ftb, status: f.status, note: 'These bytes also look like a service message — they are decoded here as a DTC because this is the DTC tab.' };
  }
  return { error: 'Enter a DTC code (P0301, P0A1F-1C) or its 2–3 raw bytes (03 01, 0A 1F 1C), optionally followed by a status byte.' };
}

const BitFieldDiagram = ({ b0, b1 }) => {
  const f = dtcBitFields(b0, b1);
  const byteBlock = (byteIdx) => (
    <div className="space-y-1.5 min-w-0">
      <div className="font-mono text-xs text-muted-foreground">Byte {byteIdx} = 0x{hex2(f.bytes[byteIdx])}</div>
      <div className="flex gap-0.5" data-testid={`dtc-bits-byte-${byteIdx}`}>
        {f.bits.slice(byteIdx * 8, byteIdx * 8 + 8).map((bit, i) => {
          const pos = byteIdx * 8 + i;
          const g = f.groups.findIndex((gr) => pos >= gr.from && pos <= gr.to);
          const shade = ['bg-autoxium-blue/25', 'bg-emerald-600/20', 'bg-amber-500/20', 'bg-violet-500/20', 'bg-rose-500/20'][g];
          return (
            <div key={i} className={`w-7 sm:w-8 h-8 flex flex-col items-center justify-center rounded border border-structure font-mono text-sm ${shade}`}>
              {bit}
              <span className="text-[8px] text-muted-foreground leading-none">{7 - i}</span>
            </div>
          );
        })}
      </div>
      <div className="flex gap-0.5">
        {f.groups.filter((gr) => gr.from >= byteIdx * 8 && gr.to < byteIdx * 8 + 8).map((gr) => {
          const width = gr.to - gr.from + 1;
          return (
            <div key={gr.from} style={{ flexGrow: width, flexBasis: 0 }} className="text-center border-t-2 border-foreground/40 pt-1 min-w-0">
              <div className="font-mono text-lg font-bold leading-none">{gr.char}</div>
              <div className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{gr.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="rounded-md border border-structure bg-muted/30 p-4 space-y-3" data-testid="dtc-bitfield">
      <SectionLabel>Bytes → bits → characters</SectionLabel>
      <div className="flex flex-wrap gap-x-6 gap-y-4">
        {byteBlock(0)}
        {byteBlock(1)}
      </div>
      <p className="text-xs text-muted-foreground">
        Bits 7–6 of byte 0 pick the letter (00 = P, 01 = C, 10 = B, 11 = U); bits 5–4 give the first digit (0–3, which is why it can never be 4–9); the remaining 12 bits are the last three hex characters.
      </p>
    </div>
  );
};

const FtbBlock = ({ ftb }) => {
  const f = decodeFtb(ftb);
  return (
    <div className="rounded-md border border-structure p-3 space-y-1.5" data-testid="dtc-ftb-block">
      <SectionLabel>Failure Type Byte (FTB) 0x{f.hex}</SectionLabel>
      <p className="text-sm"><span className="font-semibold">Category:</span> {f.category} <span className="text-muted-foreground">(high nibble 0x{hex2(ftb >> 4).slice(1)})</span></p>
      {f.categoryDetail && <p className="text-xs text-muted-foreground">{f.categoryDetail}</p>}
      {f.name ? (
        <p className="text-sm" data-testid="dtc-ftb-name"><span className="font-semibold">Sub-type:</span> {f.name}</p>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="dtc-ftb-note">{f.note}</p>
      )}
    </div>
  );
};

const DtcCodePanel = ({ seed, onOpenStatus }) => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);

  const decode = (value) => setResult(interpret(value));

  useEffect(() => {
    if (!seed) return;
    setInput(seed.text);
    setResult(seed.ftbOnly !== undefined ? { ftbOnly: seed.ftbOnly } : interpret(seed.text));
  }, [seed]);

  const info = useMemo(() => (result && result.code ? describeDtc(result.code) : null), [result]);

  const buildCopy = () => {
    const bytes = dtcCodeToBytes(result.code, result.ftb ?? 0);
    const lines = [
      `DTC: ${result.code}${result.ftb !== null && result.ftb !== undefined ? `-${hex2(result.ftb)}` : ''}`,
      `Bytes (OBD, 2-byte): ${bytesToHex(bytes.slice(0, 2))}`,
      `Bytes (UDS, 3-byte): ${bytesToHex(bytes)}`,
      `System: ${info.letterInfo.name}`,
      `Type: ${info.digit1.label}`,
      `Sub-group: ${info.characters[2].meaning}`,
      `Meaning: ${info.entry ? info.entry.description : info.unknownReason}`,
    ];
    if (info.entry) lines.push(`Typical causes: ${info.entry.causes}`);
    if (result.ftb !== null && result.ftb !== undefined) {
      const f = decodeFtb(result.ftb);
      lines.push(`FTB 0x${f.hex}: ${f.category}${f.name ? ` — ${f.name}` : ''}`);
    }
    if (result.status !== undefined) lines.push(`Status 0x${hex2(result.status)}: ${statusVerdict(result.status).primary.title}`);
    return lines.join('\n');
  };

  return (
    <div className="space-y-6" data-testid="dtc-code-panel">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">DTC Code ↔ Bytes</CardTitle>
          <CardDescription>Enter a code (P0301, P0A1F-1C) or its raw bytes (03 01, 0A 1F 1C, optionally + status byte) — it decodes in both directions.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); decode(input); }} className="flex flex-col sm:flex-row gap-2">
            <Input className="font-mono" value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. P0301 or C1 00 00" aria-label="DTC code or bytes" data-testid="dtc-code-input" />
            <Button type="submit" className="btn-premium shrink-0" data-testid="dtc-code-decode"><ScanSearch className="h-4 w-4" /> Decode</Button>
          </form>
        </CardContent>
      </Card>

      {result?.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="dtc-code-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span>{result.error}</span>
        </div>
      )}

      {result && result.ftbOnly !== undefined && (
        <Card className="technical-card" data-testid="dtc-ftb-only">
          <CardHeader><CardTitle className="font-chivo uppercase tracking-tight text-base">Failure Type Byte 0x{hex2(result.ftbOnly)}</CardTitle></CardHeader>
          <CardContent><FtbBlock ftb={result.ftbOnly} /></CardContent>
        </Card>
      )}

      {info && (
        <Card className="technical-card" data-testid="dtc-code-result">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1.5 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-2xl font-bold" data-testid="dtc-result-code">
                    {result.code}{result.ftb !== null && result.ftb !== undefined ? `-${hex2(result.ftb)}` : ''}
                  </span>
                  <Badge variant="outline" className="font-mono text-[11px]">{info.letterInfo.name}</Badge>
                  <Badge variant="outline" className={`font-mono text-[11px] ${SCOPE_BADGE[info.digit1.scope]}`} data-testid="dtc-result-scope">{SCOPE_TEXT[info.digit1.scope]}</Badge>
                </div>
                <CardDescription className="font-mono text-xs break-words">
                  OBD 2-byte: {bytesToHex(dtcCodeToBytes(result.code).slice(0, 2))} · UDS 3-byte: <span data-testid="dtc-result-uds-bytes">{bytesToHex(dtcCodeToBytes(result.code, result.ftb ?? 0))}</span>
                  {result.ftb === null || result.ftb === undefined ? ' (FTB 00 assumed)' : ''}
                </CardDescription>
              </div>
              <CopyButton getText={buildCopy} testId="dtc-code-copy" />
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {result.note && <p className="text-xs text-muted-foreground">{result.note}</p>}
            {info.entry ? (
              <div className="space-y-1.5" data-testid="dtc-result-description">
                <p className="text-base leading-relaxed">{info.entry.description}</p>
                <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Typical causes:</span> {info.entry.causes}</p>
                <p className="text-[11px] text-muted-foreground">Description written in our own words; the official SAE J2012 wording is not reproduced.</p>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-400" data-testid="dtc-result-unknown">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{info.unknownReason}</span>
              </div>
            )}

            <BitFieldDiagram b0={dtcCodeToBytes(result.code)[0]} b1={dtcCodeToBytes(result.code)[1]} />

            <div className="rounded-md border border-structure overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Char</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Meaning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {info.characters.map((ch) => (
                    <TableRow key={ch.role}>
                      <TableCell className="font-mono font-bold text-base">{ch.char}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{ch.role}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{ch.meaning}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {result.ftb !== null && result.ftb !== undefined && <FtbBlock ftb={result.ftb} />}

            {result.status !== undefined && (() => {
              const v = statusVerdict(result.status);
              return (
                <div className={`rounded-md border px-3 py-2.5 text-sm ${VERDICT_TONE[v.primary.tone]}`} data-testid="dtc-result-status">
                  <p className="font-semibold">Status 0x{hex2(result.status)}: {v.primary.title}</p>
                  <p className="text-xs mt-1 opacity-90">{v.primary.detail}</p>
                  {onOpenStatus && (
                    <Button variant="link" size="sm" className="px-0 h-auto mt-1" onClick={() => onOpenStatus(result.status)} data-testid="dtc-open-status">
                      Open 0x{hex2(result.status)} in the Status Byte tab →
                    </Button>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DtcCodePanel;
